import { useLocale } from '@ecomm/data-hooks'
import { useNinetailedFeatureFlag } from '@ecomm/shared-ninetailed'
import { useMemo } from 'react'
import UAParser from 'ua-parser-js'

// Checks if the current environment is a Cypress test environment
const isCypressTest = (w: Window) => !!w && Object.hasOwn(w, 'Cypress')

export const BMSPathname = '/build-my-system'

const NINETAILED_CTFL_ID_CONTROL = '6165uB0dssgjXBp6qu8RsV'

/**
 * Custom hook for ECP-9501 experiment to determine if BMS flows should open in a new tab.
 *
 * This experiment enables opening certain flows in a new tab to improve user experience.
 * The hook checks multiple conditions to ensure the feature is only active for supported
 * environments and user configurations.
 *
 * Conditions for activation:
 * - Only works for US locale (en-US)
 * - Only works on desktop devices (non-mobile/tablet)
 * - Excludes desktop Safari browsers
 * - Requires experiment flag to be active
 */
export const useShouldOpenPackageInNewTab = (): boolean => {
  const { isVariant } = useNinetailedFeatureFlag({
    baselineId: NINETAILED_CTFL_ID_CONTROL
  })

  const locale = useLocale()

  const isExperimentedActivated = useMemo(() => {
    if (!isVariant) {
      return false
    }

    // Parse user agent to detect device and browser information
    const parser = new UAParser()

    // Check if user is on desktop (UAParser only sets device.type for mobile/tablet)
    const isDesktop = !parser.getDevice().type

    // Detect Safari browser (excluded due to potential compatibility issues)
    const isDesktopSafari = parser.getBrowser().name === 'Safari'

    // Only activate for US users
    const isUs = locale === 'en-US'

    return (
      isUs && isDesktop && !isDesktopSafari && !isCypressTest(globalThis.window)
    )
  }, [locale, isVariant])

  return isExperimentedActivated
}
